import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Eye, UserCog } from "lucide-react";
import { useAuth } from "../lib/auth-context";
import { useToast } from "../lib/toast";

const DEMO_ROLES: Array<{ key: "supplier" | "planner" | "compliance" | "admin"; label: string }> = [
  { key: "supplier", label: "Supplier — Lin Wei" },
  { key: "planner", label: "SBD Planner — Sarah" },
  { key: "compliance", label: "Compliance — Ricardo" },
  { key: "admin", label: "Administrator — James" },
];

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const { login, loginDemo, loginEntra } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch {
      toast("Invalid email or password", "error");
    } finally {
      setLoading(false);
    }
  }

  async function demo(roleKey: (typeof DEMO_ROLES)[number]["key"]) {
    try {
      await loginDemo(roleKey);
      navigate("/");
    } catch {
      toast("Sign-in is unavailable right now", "error");
    }
  }

  async function entraSignIn() {
    setSsoLoading(true);
    try {
      await loginEntra();
      navigate("/");
    } catch {
      toast("Entra ID sign-in is unavailable right now", "error");
    } finally {
      setSsoLoading(false);
    }
  }

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2 text-slate-900">
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-sbd-black text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #FFC72C22, transparent 40%), radial-gradient(circle at 80% 60%, #FFC72C15, transparent 45%), radial-gradient(circle at 50% 90%, #ffffff10, transparent 40%)" }}
        />
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-sbd-yellow flex items-center justify-center font-black text-sbd-black text-lg">SB</div>
          <div className="font-extrabold tracking-tight text-lg">STANLEY BLACK &amp; DECKER</div>
          <div className="h-6 w-px bg-white/20 mx-1" />
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center font-black text-white text-sm">Y</div>
            <div className="text-xs font-semibold text-white/70 leading-tight">
              YASH
              <br />
              <span className="font-normal text-white/40">Technologies</span>
            </div>
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <div className="inline-flex items-center gap-2 text-xs font-semibold text-sbd-yellow bg-white/5 border border-white/10 rounded-full px-3 py-1 mb-6">
            <Sparkles className="w-3.5 h-3.5" /> Modernized Global Supplier Portal
          </div>
          <h1 className="text-4xl font-extrabold leading-tight mb-4">One portal. Every supplier workflow. Zero page reloads.</h1>
          <p className="text-white/60 text-sm leading-relaxed">
            A ground-up rebuild of SBD's Global Supplier Portal — the same 13 modules (PO/SLA confirmation, ASN, production order tracking, vendor capacity,
            compliance and admin) delivered as a single modern application on Cloudflare, with role-aware, real-time workflows.
          </p>
          <div className="mt-8 flex gap-6 text-sm">
            <div>
              <div className="text-2xl font-bold text-sbd-yellow">13</div>
              <div className="text-white/50">modules</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-sbd-yellow">2×</div>
              <div className="text-white/50">clicks vs 5 pages</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-sbd-yellow">100%</div>
              <div className="text-white/50">mobile ready</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 text-xs text-white/40">Built by YASH Technologies</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10 bg-slate-50">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <div className="h-8 w-8 rounded-lg bg-sbd-yellow flex items-center justify-center font-black text-sbd-black text-sm">SB</div>
            <span className="font-bold text-sm">GSP</span>
          </div>
          <h2 className="text-2xl font-bold mb-1">Welcome back</h2>
          <p className="text-sm text-slate-500 mb-6">Sign in to your supplier or SBD account</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-slate-600">Email or username</label>
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required className="mt-1 w-full rounded-lg border border-slate-200 bg-white text-slate-900 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sbd-yellow/60" />
            </div>
            <div>
              <div className="flex justify-between items-center">
                <label className="text-xs font-semibold text-slate-600">Password</label>
                <a href="/forgot-password" className="text-xs text-slate-500 hover:text-sbd-black">
                  Forgot password?
                </a>
              </div>
              <div className="relative mt-1">
                <input value={password} onChange={(e) => setPassword(e.target.value)} type={showPwd ? "text" : "password"} required className="w-full rounded-lg border border-slate-200 bg-white text-slate-900 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sbd-yellow/60" />
                <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600">
                  <Eye className="w-4 h-4" />
                </button>
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-500">
              <input type="checkbox" defaultChecked className="rounded border-slate-300" /> Remember me on this device
            </label>
            <button type="submit" disabled={loading} className="w-full rounded-lg bg-sbd-black text-white font-semibold text-sm py-2.5 hover:opacity-90 transition disabled:opacity-60">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px bg-slate-200 flex-1" />
            <span className="text-xs text-slate-400">or</span>
            <div className="h-px bg-slate-200 flex-1" />
          </div>

          <button
            onClick={entraSignIn}
            disabled={ssoLoading}
            className="w-full rounded-lg border border-slate-300 text-slate-900 font-semibold text-sm py-2.5 flex items-center justify-center gap-2 hover:bg-slate-100 transition disabled:opacity-60"
          >
            <svg width="16" height="16" viewBox="0 0 21 21">
              <rect x="1" y="1" width="9" height="9" fill="#f25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
              <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
            {ssoLoading ? "Redirecting…" : "Continue with Entra ID"}
          </button>

          <div className="mt-8 rounded-xl border border-dashed border-slate-300 p-4">
            <div className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
              <UserCog className="w-3.5 h-3.5" /> Sign in as…
            </div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO_ROLES.map((r) => (
                <button key={r.key} onClick={() => demo(r.key)} className="text-xs font-medium text-slate-700 rounded-lg border border-slate-200 py-2 hover:border-sbd-yellow hover:bg-sbd-yellow/10">
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <p className="text-center text-xs text-slate-400 mt-6">
            Register as a new supplier?{" "}
            <a href="/register" className="text-sbd-black font-medium">
              Start registration
            </a>
          </p>
          <p className="text-center text-[11px] text-slate-300 mt-8 flex items-center justify-center gap-1.5">
            <span className="h-4 w-4 rounded bg-slate-200 flex items-center justify-center font-bold text-[9px] text-slate-500">Y</span> Built by YASH Technologies
          </p>
        </div>
      </div>
    </div>
  );
}
